import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileCheck, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/context/LanguageContext';

const Home = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: <FileCheck className="w-8 h-8 text-[#009FB7]" />,
      title: t.home.feature1Title,
      description: t.home.feature1Desc
    },
    {
      icon: <Clock className="w-8 h-8 text-[#009FB7]" />,
      title: t.home.feature2Title,
      description: t.home.feature2Desc
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-[#009FB7]" />,
      title: t.home.feature3Title,
      description: t.home.feature3Desc
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1A4D7F] via-[#1A4D7F] to-[#153e66] text-white">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1726533765275-a69cfd7f9897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxEdWJhaSUyMHNreWxpbmUlMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzY5MzU4MTQwfDA&ixlib=rb-4.1.0&q=85"
            alt="Dubai skyline"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container mx-auto px-6 py-20 md:py-32 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_5769230e-7a66-4b7d-9294-6503e125a0b4/artifacts/muqtjev5_IMG_5362.jpeg" 
                alt="A-One Tourism Logo" 
                className="w-16 h-16 rounded-full object-cover border-2 border-white/30 bg-white/10 backdrop-blur-xl"
              />
              <div>
                <h1 className="text-2xl font-heading font-bold">A-ONE TOURISM</h1>
                <p className="text-blue-100 text-sm">{t.home.heroSubtitle}</p>
              </div>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-heading font-bold mb-6 tracking-tight" data-testid="hero-heading">
              {t.home.heroTitle}
            </h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              {t.home.heroSubtitle}
            </p>
            
            <div className="flex flex-row gap-4">
              <Link to="/apply" data-testid="apply-now-button">
                <Button size="lg" className="bg-white text-[#1A4D7F] hover:bg-blue-50 font-semibold px-6 sm:px-8 py-4 text-base sm:text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center">
                  {t.home.getStarted}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/track" data-testid="track-application-button">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-semibold px-6 sm:px-8 py-4 text-base sm:text-lg flex items-center justify-center">
                  {t.home.trackApplication}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#1A4D7F] mb-4 tracking-tight">
              {t.home.whyChooseUs}
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              {t.home.feature4Desc}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`feature-card-${index}`}
              >
                <Card className="border-slate-200 hover:shadow-lg transition-all h-full hover:-translate-y-1 duration-200">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-[#1A4D7F] mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1A4D7F] to-[#009FB7] text-white">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              {t.home.heroTitle}
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              {t.home.heroSubtitle}
            </p>
            <Link to="/apply" data-testid="cta-apply-button">
              <Button size="lg" className="bg-white text-[#1A4D7F] hover:bg-blue-50 font-semibold px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all">
                {t.home.getStarted}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
